import { Autocomplete, Box, Chip, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import StatusChip from '@sophocles/components/StatusChip';
import { fetchAllRelayControllerFirmwareVersions } from '@sophocles/entities/RelayControllerFirmwareVersion/relayControllerFirmwareVersionActions';
import {
  filterFirmwareVersionOptions,
  hasKnownModelCompatibility,
} from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareVersionOptions';

const compareVersions = (a, b) => {
  const av = String(a.get('version', ''));
  const bv = String(b.get('version', ''));
  return bv.localeCompare(av, undefined, { numeric: true });
};

const CAPABILITY_FLAGS = [
  ['supports_v2_protocol', 'v2'],
  ['supports_relay_monitor', 'relay-monitor'],
  ['supports_negative_schedule_offsets', 'neg-offsets'],
  ['supports_day_of_week_schedules', 'dow'],
];

const capabilityChips = option =>
  CAPABILITY_FLAGS.filter(([key]) => option && option[key]).map(([, label]) => (
    <Chip key={label} size="small" label={label} sx={{ mr: 0.5 }} />
  ));

/**
 * Engineering firmware-upgrade autocomplete.
 * Hits /relaycontrollerfirmwareversions and renders rich items
 * (version + release notes + capability flags).
 *
 * Two independent filters narrow the options:
 *
 * - **Model** (``modelId``, hard): a version whose hardware models exclude
 *   the target is never offered — sending it would brick the unit. Versions
 *   the derivation resolved to no models are of unknown compatibility; they
 *   stay on offer and carry a "Compatibility unknown" badge.
 * - **Major release** (``restrictMajor`` + ``currentMajor``, liftable): the
 *   "Current: v…" readout and the danger toggle that lifts it live in the
 *   calling dialog so this component stays a stateless picker. Lifting it
 *   cannot reveal a version for a different model.
 */
const FirmwareVersionAutocomplete = ({
  value,
  onChange,
  label = 'Firmware version',
  size = 'small',
  currentMajor = null,
  restrictMajor = false,
  modelId = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const versions = useSelector(state => state.entities.relayControllerFirmwareVersions);

  useEffect(() => {
    let cancelled = false;
    // has_binary=true excludes versions whose upgrade_file is blank or
    // null — those can't actually be dispatched, so they shouldn't be
    // pickable here. The matrix view (which wants the full list) uses
    // a separate fetch path.
    dispatch(fetchAllRelayControllerFirmwareVersions({ has_binary: true })).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const options = useMemo(() => {
    if (!versions) return [];
    // Defense in depth: even if a non-binary version is in the redux
    // cache from another fetcher (e.g. RmaHOC), only show ones we can
    // actually upgrade to.
    const all = versions
      .toList()
      .filter(v => Boolean(v.get('upgrade_file', null)))
      .sort(compareVersions)
      .toJS();
    return filterFirmwareVersionOptions(all, { modelId, restrictMajor, currentMajor });
  }, [versions, restrictMajor, currentMajor, modelId]);

  return (
    <Autocomplete
      size={size}
      loading={loading}
      options={options}
      value={value || null}
      onChange={(_, next) => onChange(next || null)}
      isOptionEqualToValue={(option, current) => option && current && option.id === current.id}
      getOptionLabel={option => (option ? `v${option.version}` : '')}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Stack spacing={0.25} sx={{ width: '100%' }}>
            <Typography variant="body2">v{option.version}</Typography>
            {option.release_notes ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.release_notes}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {capabilityChips(option)}
              {hasKnownModelCompatibility(option) ? null : (
                <StatusChip
                  color="neutral"
                  dot={false}
                  label={t('firmware.compatibilityUnknown', {
                    defaultValue: 'Compatibility unknown',
                  })}
                />
              )}
            </Stack>
          </Stack>
        </Box>
      )}
      renderInput={params => <TextField {...params} label={label} placeholder="Select firmware version" />}
    />
  );
};

export default FirmwareVersionAutocomplete;
